'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import { LayoutDashboard, Users, Package, List, Settings, ChevronRight, X, Sparkles, Building2, ClipboardList, BarChart2 } from 'lucide-react';
import { useCompany } from '@/contexts/CompanyContext';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { id: 'nav-customers', label: 'Customers', href: '/customer-management', icon: Users },
  { id: 'nav-products', label: 'Products', href: '/product-management', icon: Package },
  { id: 'nav-quotations', label: 'Quotations', href: '/quotation-generator', icon: ClipboardList },
  { id: 'nav-invoices', label: 'Invoices', href: '/invoice-list', icon: List },
  { id: 'nav-reports', label: 'Reports', href: '/reports', icon: BarChart2 },
  { id: 'nav-settings', label: 'Settings', href: '/settings', icon: Settings },
];

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  activePath: string;
}

export default function Sidebar({ collapsed, mobileOpen, onMobileClose, activePath }: SidebarProps) {
  const isActive = (href: string) => {
    if (!activePath) return false;
    if (href === '/dashboard' && activePath === '/') return true;
    return activePath === href || activePath.startsWith(href + '/');
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col h-full bg-card border-r border-border sidebar-transition z-30
          ${collapsed ? 'w-16' : 'w-60'}
        `}
      >
        <SidebarContent collapsed={collapsed} activePath={activePath} isActive={isActive} />
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-full w-72 bg-card border-r border-border z-50 flex flex-col
          transition-transform duration-300 ease-spring lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-bold text-xl text-foreground">NovaInvoice</span>
          </div>
          <button onClick={onMobileClose} className="btn-ghost p-2">
            <X size={18} />
          </button>
        </div>
        <SidebarContent collapsed={false} activePath={activePath} isActive={isActive} />
      </aside>
    </>
  );
}

function SidebarContent({
  collapsed,
  activePath,
  isActive,
}: {
  collapsed: boolean;
  activePath: string;
  isActive: (href: string) => boolean;
}) {
  const { activeCompany, subscription } = useCompany();

  return (
    <>
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center px-0' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div>
            <span className="font-bold text-lg text-foreground leading-none">NovaInvoice</span>
            {activeCompany && (
              <p className="text-2xs text-muted-foreground mt-0.5 truncate max-w-[140px]">{activeCompany.companyName}</p>
            )}
          </div>
        )}
      </div>

      {/* Active company badge (collapsed) */}
      {collapsed && activeCompany && (
        <div className="flex justify-center py-2 border-b border-border">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-800"
            style={{ backgroundColor: activeCompany.themeColor }}
            title={activeCompany.companyName}
          >
            {activeCompany.companyName.charAt(0).toUpperCase()}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <p className="text-2xs font-600 uppercase tracking-widest text-muted-foreground px-3 pb-2">
            Main Menu
          </p>
        )}
        {navItems.map((item) => {
          const NavIcon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.id}
              href={item.href}
              className={`nav-item ${active ? 'active' : ''} ${collapsed ? 'justify-center px-0 py-2.5' : ''} relative group`}
              title={collapsed ? item.label : undefined}
            >
              <NavIcon size={18} className="shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <span className="px-1.5 py-0.5 rounded-full text-2xs font-700 bg-primary text-primary-foreground">
                      {item.badge}
                    </span>
                  )}
                  {active && <ChevronRight size={14} className="text-primary" />}
                </>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-0 right-0 w-2 h-2 rounded-full bg-primary" />
              )}
              {/* Tooltip for collapsed */}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 rounded-md bg-foreground text-background text-xs font-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {item.label}
                </div>
              )}
            </Link>
          );
        })}

        {/* Switch Company link */}
        {!collapsed && (
          <div className="pt-2 mt-2 border-t border-border">
            <Link
              href="/select-company"
              className="nav-item relative group"
            >
              <Building2 size={18} className="shrink-0" />
              <span className="flex-1">Switch Company</span>
            </Link>
          </div>
        )}
      </nav>

      {/* Bottom */}
      {!collapsed && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2 p-2 rounded-xl bg-gradient-to-r from-primary/10 to-accent/5">
            <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center shrink-0">
              <Sparkles size={14} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-600 text-foreground truncate">{subscription.subscriptionPlan} Plan</p>
              <p className="text-2xs text-muted-foreground">
                {subscription.maxCompanies === -1 ? 'Unlimited companies' : `${subscription.maxCompanies} companies max`}
              </p>
            </div>
          </div>
          {activeCompany && (
            <div className="flex items-center gap-2 mt-3 px-1">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-700 shrink-0"
                style={{ backgroundColor: activeCompany.themeColor }}
              >
                {activeCompany.companyName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-500 text-foreground truncate">{activeCompany.companyName}</p>
                <p className="text-2xs text-muted-foreground truncate">{activeCompany.companyType}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}